import type { BuildProgressSnapshot, WatchBuildResult, WatchPart } from './api/client'

export type StudioPhase =
  | 'drafting'
  | 'understanding'
  | 'selecting_parts'
  | 'checking_compatibility'
  | 'cache_lookup'
  | 'ready'
  | 'needs_adjustment'
  | 'rendering_preview'

export type ImageLookupState = 'idle' | 'looking' | 'found' | 'missing' | 'unavailable'

export type StudioState = {
  phase: StudioPhase
  label: string
  detail: string
  selectedCount: number
  totalCount: number
  activePart: string | null
  isWorking: boolean
  isUpdating: boolean
  canReviewBuild: boolean
}

type DeriveStudioStateInput = {
  isSending: boolean
  statusLabel?: string
  progress: BuildProgressSnapshot | null
  buildResult: WatchBuildResult | null
  error: string | null
  imageLookup: ImageLookupState
  isGeneratingImage: boolean
}

export const CORE_SLOTS = ['case', 'movement', 'dial', 'hands', 'strap'] as const

export function isCompleteCompatibleBuild(buildResult: WatchBuildResult): boolean {
  return (
    buildResult.failed_steps.length === 0 &&
    buildResult.compatibility_errors.length === 0 &&
    CORE_SLOTS.every((partType) => buildResult.build.parts[partType])
  )
}

export function studioPhaseForProgress(
  progress: BuildProgressSnapshot | null,
): Extract<StudioPhase, 'understanding' | 'selecting_parts' | 'checking_compatibility'> {
  switch (progress?.phase) {
    case 'building':
      return 'selecting_parts'
    case 'resolve_conflicts':
    case 'finalizing':
      return 'checking_compatibility'
    case 'sizing':
    default:
      return 'understanding'
  }
}

function defaultWorkingLabel(
  progress: BuildProgressSnapshot | null,
  phase: StudioPhase,
  isUpdating: boolean,
): string {
  switch (progress?.phase) {
    case 'sizing':
      return 'Calibrating watch size'
    case 'resolve_conflicts':
      return 'Adjusting for compatibility'
    case 'finalizing':
      return 'Finalizing your build'
    default:
      if (phase === 'selecting_parts') {
        return isUpdating ? 'Updating your selected parts' : 'Selecting compatible parts'
      }
      return isUpdating ? 'Understanding your refinement' : 'Understanding your request'
  }
}

function selectedProgressCount(progress: BuildProgressSnapshot | null): number {
  if (!progress) return 0
  return CORE_SLOTS.filter((slot) => {
    const status = progress.slots[slot]?.status
    return status === 'selected' || status === 'locked'
  }).length
}

function formatPartName(value: string | null): string | null {
  if (!value) return null
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function deriveStudioState({
  isSending,
  statusLabel,
  progress,
  buildResult,
  error,
  imageLookup,
  isGeneratingImage,
}: DeriveStudioStateInput): StudioState {
  const resultCount = buildResult ? Object.keys(buildResult.build.parts).length : 0
  const selectedCount = progress ? selectedProgressCount(progress) : resultCount
  const totalCount = Math.max(
    CORE_SLOTS.length,
    buildResult ? resultCount + buildResult.failed_steps.length : CORE_SLOTS.length,
  )
  const isUpdating = isSending && buildResult !== null
  const activePart = formatPartName(progress?.active_slot ?? null)

  if (isGeneratingImage) {
    return {
      phase: 'rendering_preview',
      label: 'Generating studio preview',
      detail: 'Your selected parts remain available while the optional image is created.',
      selectedCount,
      totalCount,
      activePart: null,
      isWorking: true,
      isUpdating: false,
      canReviewBuild: buildResult !== null,
    }
  }

  if (error) {
    return {
      phase: 'needs_adjustment',
      label: 'Build paused',
      detail: 'Your previous progress is safe. Retry or adjust your request.',
      selectedCount,
      totalCount,
      activePart,
      isWorking: false,
      isUpdating: false,
      canReviewBuild: buildResult !== null,
    }
  }

  if (isSending) {
    const phase = studioPhaseForProgress(progress)
    const defaultLabel = defaultWorkingLabel(progress, phase, isUpdating)

    return {
      phase,
      label: progress?.label || statusLabel || defaultLabel,
      detail:
        progress?.phase === 'finalizing'
          ? 'Verifying the final selection before it is shown.'
          : activePart
            ? `Currently working on ${activePart.toLowerCase()}.`
            : isUpdating
              ? 'Your previous build stays visible until the update is ready.'
              : 'Horatix will reveal each selected part as it is confirmed.',
      selectedCount,
      totalCount,
      activePart,
      isWorking: true,
      isUpdating,
      canReviewBuild: buildResult !== null,
    }
  }

  if (buildResult && imageLookup === 'looking') {
    return {
      phase: 'cache_lookup',
      label: 'Checking for an exact saved preview',
      detail: 'Your parts and build summary are ready now.',
      selectedCount,
      totalCount,
      activePart: null,
      isWorking: true,
      isUpdating: false,
      canReviewBuild: true,
    }
  }

  if (buildResult) {
    const needsAdjustment = !isCompleteCompatibleBuild(buildResult)
    return {
      phase: needsAdjustment ? 'needs_adjustment' : 'ready',
      label: needsAdjustment ? 'Build needs adjustment' : 'Your build is ready',
      detail: needsAdjustment
        ? resultCount > 0
          ? `${resultCount} part${resultCount === 1 ? '' : 's'} selected; review the remaining issues.`
          : 'No compatible parts were selected. Try a broader refinement.'
        : imageLookup === 'found'
          ? 'An exact saved preview and all selected parts are ready.'
          : 'Review your selected parts or optionally generate a studio preview.',
      selectedCount,
      totalCount,
      activePart: null,
      isWorking: false,
      isUpdating: false,
      canReviewBuild: true,
    }
  }

  return {
    phase: 'drafting',
    label: 'Start with your watch brief',
    detail: 'Share a style, occasion, size, or budget.',
    selectedCount: 0,
    totalCount: CORE_SLOTS.length,
    activePart: null,
    isWorking: false,
    isUpdating: false,
    canReviewBuild: false,
  }
}

function selectedVariantIdentity(part: WatchPart): string {
  const metadata = part.metadata ?? {}
  const selected =
    metadata.selected_variant_id ??
    metadata.default_variant_id ??
    metadata.variant_id ??
    ''

  if (selected !== '') return String(selected)

  const selectedVariant = metadata.selected_variant
  if (selectedVariant && typeof selectedVariant === 'object') {
    return JSON.stringify(selectedVariant)
  }
  if (typeof metadata.selected_image_url === 'string') {
    return `image=${metadata.selected_image_url}`
  }
  return ''
}

export function buildIdentity(result: WatchBuildResult): string {
  const parts = Object.entries(result.build.parts)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(
      ([partType, part]) =>
        `${partType}:${part.id}:variant=${selectedVariantIdentity(part)}`,
    )
    .join('|')
  const failures = result.failed_steps
    .map((failure) => failure.part_type)
    .sort()
    .join('|')
  const errors = [...result.compatibility_errors].sort().join('|')
  return `${parts}::${failures}::${result.watch_size?.selected_size ?? ''}::${errors}`
}
